import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container, Button, Dropdown } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Menu, X } from 'lucide-react';
import './Header.scss';
import logo from '../../../assets/images/tekstaslogo.png';


const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isExhibitionPage = location.pathname.includes('/exhibitions') || location.pathname.includes('/exhibition/');
  const isHomePage = location.pathname === '/';
  const isInnerPage = !isHomePage;

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setMobileMenuOpen(false); // Close mobile menu when nav item is clicked
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (isExhibitionPage) {
        setIsScrolled(true);
      } else {
        setIsScrolled(scrollPosition > 50);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isExhibitionPage]);

  // Close mobile menu when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const currentLanguage = i18n.language;

  return (
    <>
      <Navbar expand="lg" className={`navbar-dark ${isScrolled ? 'scrolled' : ''} ${isInnerPage ? 'inner-page' : ''} ${isExhibitionPage ? 'exhibition-page' : ''}`}>
        <Container className={isInnerPage ? 'inner-page-container' : ''}>
          <Navbar.Brand as={Link} to="/">
            <img src={logo} alt="Tekstas Logo" />
          </Navbar.Brand>
          <button 
            className="navbar-toggler"
            type="button"
            aria-controls="basic-navbar-nav" 
            onClick={handleMobileMenuToggle}
          >
            {mobileMenuOpen ? 
              <X size={24} color={isExhibitionPage ? "black" : "white"} /> : 
              <Menu size={24} color={isExhibitionPage ? "black" : "white"} />
            }
          </button>
          <Navbar.Collapse id="basic-navbar-nav" className={mobileMenuOpen ? 'show' : ''}>
            <Nav className="mx-auto">
              <Nav.Link as={Link} to="/" onClick={closeMobileMenu}>{t('nav.home')}</Nav.Link>
              <Nav.Link href="#about" onClick={(e) => handleNavClick(e, 'about')}>{t('nav.about')}</Nav.Link>
              <Nav.Link href="#certificates" onClick={(e) => handleNavClick(e, 'certificates')}>{t('nav.certificates')}</Nav.Link>
              <Nav.Link as={Link} to="/careers" onClick={closeMobileMenu}>{t('nav.careers')}</Nav.Link>
              <Nav.Link as={Link} to="/contact" onClick={closeMobileMenu}>{t('nav.contact')}</Nav.Link>
              
              <Nav.Link as={Link} to="/exhibitions" onClick={closeMobileMenu}>{t('nav.exhibitions')}</Nav.Link>
            </Nav>
            <div className="d-flex align-items-center gap-3">
              {/* Language Switcher */}
              <Dropdown>
                <Dropdown.Toggle 
                  variant="outline-light" 
                  size="sm"
                  className="language-btn"
                >
                  <Globe size={14} />
                  {currentLanguage === 'tr' ? 'TR' : 
                   currentLanguage === 'de' ? 'DE' :
                   currentLanguage === 'es' ? 'ES' :
                   currentLanguage === 'fr' ? 'FR' : 'EN'}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item 
                    onClick={() => changeLanguage('tr')}
                    active={currentLanguage === 'tr'}
                  >
                    🇹🇷 {t('language.turkish')}
                  </Dropdown.Item>
                  <Dropdown.Item 
                    onClick={() => changeLanguage('en')}
                    active={currentLanguage === 'en'}
                  >
                    🇺🇸 {t('language.english')}
                  </Dropdown.Item>
                  <Dropdown.Item 
                    onClick={() => changeLanguage('de')}
                    active={currentLanguage === 'de'}
                  >
                    🇩🇪 {t('language.german')}
                  </Dropdown.Item>
                  <Dropdown.Item 
                    onClick={() => changeLanguage('es')}
                    active={currentLanguage === 'es'}
                  >
                    🇪🇸 {t('language.spanish')}
                  </Dropdown.Item>
                  <Dropdown.Item 
                    onClick={() => changeLanguage('fr')}
                    active={currentLanguage === 'fr'}
                  >
                    🇫🇷 {t('language.french')}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
    
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      
      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'show' : ''}`}
        onClick={closeMobileMenu}
      />
    </>
  );
};

export default Header;